import { useCallback, useMemo } from 'react'

import { UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { useRoomTableStore } from 'src/hooks/stores'
import { useRoomTableFiltering } from 'src/hooks/useRoomTableFiltering'
import { useRoomTableSort } from 'src/hooks/useRoomTableSort'
import {
  RoomItemBasedFilteringItemType,
  RoomItemBasedFilteringKey,
} from 'src/stores/roomTableFilteringStore'
import { RoomTranslator } from 'src/translators'
import {
  ApplicationStatus,
  BulkUpdateRoomsFormEntity,
  RegistrationStatus,
  RoomEntity,
  RoomTypeEntity,
  SelectMasterEntity,
  WingDetailEntity,
} from 'src/types'

type LoadExcludeKeysFromFilteredRoomsInput<T extends RoomEntity[] | Partial<RoomEntity>[]> = {
  targetRooms: T
  keys: RoomItemBasedFilteringKey[]
}

type UpdateWatchedRoomsInput = {
  watch: UseFormWatch<BulkUpdateRoomsFormEntity>
  wings: WingDetailEntity[]
  roomTypes: RoomTypeEntity[]
  setValue: UseFormSetValue<BulkUpdateRoomsFormEntity>
  itemBasedFiltering: RoomItemBasedFilteringItemType | undefined
  rangeFilterRegisterLimitAt: { start?: Date; end?: Date } | undefined
}

type BatchUpdateWatchedRoomsWithTextInput = {
  watch: UseFormWatch<BulkUpdateRoomsFormEntity>
  setValue: UseFormSetValue<BulkUpdateRoomsFormEntity>
  text: string
  key: 'roomName' | 'floor' | 'memo'
}

type BatchUpdateWatchedRoomsWithDateInput = {
  watch: UseFormWatch<BulkUpdateRoomsFormEntity>
  setValue: UseFormSetValue<BulkUpdateRoomsFormEntity>
  date: Date | undefined
  key: 'registrationDueDate'
}
type BatchUpdateWatchedRoomsWithBooleanInput = {
  watch: UseFormWatch<BulkUpdateRoomsFormEntity>
  setValue: UseFormSetValue<BulkUpdateRoomsFormEntity>
  value: boolean
  key: 'isSelectEnabled'
}
type BatchUpdateWatchedRoomsWithSelectMasterInput = {
  watch: UseFormWatch<BulkUpdateRoomsFormEntity>
  setValue: UseFormSetValue<BulkUpdateRoomsFormEntity>
  selectMaster: SelectMasterEntity
  isNewValue: boolean
}

export const useRoomTable = () => {
  const { roomTableStoreValue, ...roomTableStore } = useRoomTableStore()
  const {
    loadFilteredRooms,
    searchWord,
    filteredRoomIds,
    setFilteredRoomIds,
    itemBasedFiltering,
    rangeFilterRegisterLimitAt,
    resetFilteringItems,
  } = useRoomTableFiltering()
  const { sortItem } = useRoomTableSort()

  // 引数のkeysで指定された項目はフィルタリングしない。それ以外の項目でフィルタリングした住戸一覧を返す
  const loadExcludeKeysFromFilteredRooms = useCallback(
    <T extends RoomEntity[] | Partial<RoomEntity>[]>(
      input: LoadExcludeKeysFromFilteredRoomsInput<T>
    ) => {
      const { targetRooms, keys } = input

      return loadFilteredRooms<T>({
        rooms: targetRooms,
        itemBasedFiltering,
        rangeFilterRegisterLimitAt,
        excludeKeys: keys,
      })
    },
    [itemBasedFiltering, loadFilteredRooms, rangeFilterRegisterLimitAt]
  )

  // 項目別のフィルタリング機能も含めてフィルタリングした住戸一覧
  const getFilteredRooms = useCallback(
    (rooms: RoomEntity[]) => {
      return loadFilteredRooms({ rooms, itemBasedFiltering, rangeFilterRegisterLimitAt })
    },
    [itemBasedFiltering, loadFilteredRooms, rangeFilterRegisterLimitAt]
  )

  const getSortedRooms = useCallback(
    (targetRooms: RoomEntity[]) => {
      if (!sortItem) {
        return targetRooms
      }
      return [...targetRooms].sort((a, b) => {
        if (sortItem.col === 'roomName') {
          // バックエンド側は住戸名でのソートを行っているため、バックエンドの並び順を利用する
          return sortItem.type === 'asc' ? 1 : -1
        }
        if (sortItem.col === 'wing') {
          const aValue = a.wing?.name ?? ''
          const bValue = b.wing?.name ?? ''
          const returnValue = aValue.localeCompare(bValue, 'ja')
          return sortItem.type === 'asc' ? returnValue : -returnValue
        }
        if (sortItem.col === 'floor') {
          const numberA = Number(a.floor)
          const numberB = Number(b.floor)
          const isNumberA = !Number.isNaN(numberA)
          const isNumberB = !Number.isNaN(numberB)

          if (isNumberA && isNumberB) {
            // 両方が数値として解釈可能な場合、数値として比較
            return sortItem.type === 'asc' ? numberA - numberB : numberB - numberA
          }
          if (!isNumberA && !isNumberB) {
            // 両方が文字列の場合、文字列の辞書順でソート
            return sortItem.type === 'asc'
              ? a.floor.localeCompare(b.floor)
              : b.floor.localeCompare(a.floor)
          }
          if (isNumberA && !isNumberB) {
            // aが数値、bが文字列の場合、aを先に表示
            return sortItem.type === 'asc' ? -1 : 1
          }
          // aが文字列、bが数値の場合、bを先に表示
          return sortItem.type === 'asc' ? 1 : -1
        }
        if (sortItem.col === 'roomType') {
          const aValue = a.roomType?.name ?? ''
          const bValue = b.roomType?.name ?? ''
          const returnValue = aValue.localeCompare(bValue, 'ja')
          return sortItem.type === 'asc' ? returnValue : -returnValue
        }
        if (sortItem.col === 'selectEnabled') {
          const aValue = a.isSelectEnabled ?? false
          const bValue = b.isSelectEnabled ?? false
          // セレクト対象外は表示する際に実際の値の逆を表示する形になるため、他booleanのソートとは逆の処理を行う
          return sortItem.type === 'asc' ? (aValue ? -1 : 1) : bValue ? -1 : 1
        }
        if (sortItem.col === 'memo') {
          const aValue = a.memo ?? ''
          const bValue = b.memo ?? ''
          const returnValue = aValue.localeCompare(bValue, 'ja')
          return sortItem.type === 'asc' ? returnValue : -returnValue
        }
        if (sortItem.col === 'selectMaster') {
          const aValue = a.select.selectedItemIds.includes(sortItem.selectMasterId)
          const bValue = b.select.selectedItemIds.includes(sortItem.selectMasterId)
          return sortItem.type === 'asc' ? (aValue ? 1 : -1) : bValue ? 1 : -1
        }

        if (sortItem.col === 'registerLimitAt') {
          const aValue = a.select.registerLimitAt ? a.select.registerLimitAt.getTime() : -1
          const bValue = b.select.registerLimitAt ? b.select.registerLimitAt.getTime() : -1
          return sortItem.type === 'asc' ? aValue - bValue : bValue - aValue
        }
        if (['publishApplication', 'orderApplication'].includes(sortItem.col)) {
          const loadApplicationValue = (s: ApplicationStatus) => {
            switch (s) {
              case 'waiting': {
                return 1
              }
              case 'reviewing': {
                return 2
              }
              case 'approved': {
                return 3
              }
              case 'rejected': {
                return 4
              }
              case 'canceled': {
                return 5
              }
              case 'approvalCanceled': {
                return 6
              }
              default: {
                return 0
              }
            }
          }

          const key =
            sortItem.col === 'publishApplication'
              ? 'publishApplicationRequestStatus'
              : 'orderApplicationRequestStatus'
          const aValue = loadApplicationValue(a[key])
          const bValue = loadApplicationValue(b[key])
          return sortItem.type === 'asc' ? aValue - bValue : bValue - aValue
        }
        if (sortItem.col === 'contractorName') {
          const aValue = a.contractor?.name ?? ''
          const bValue = b.contractor?.name ?? ''
          const returnValue = aValue.localeCompare(bValue, 'ja')
          return sortItem.type === 'asc' ? returnValue : -returnValue
        }
        if (sortItem.col === 'contractorConfirmed') {
          const aValue = a.contractor?.isConfirmed ?? false
          const bValue = b.contractor?.isConfirmed ?? false
          return sortItem.type === 'asc' ? (aValue ? 1 : -1) : bValue ? 1 : -1
        }
        if (sortItem.col === 'emailSentTime') {
          const aValue = a.contractor?.selectEmailSentTime ?? -1
          const bValue = b.contractor?.selectEmailSentTime ?? -1
          return sortItem.type === 'asc' ? aValue - bValue : bValue - aValue
        }
        if (sortItem.col === 'agreementRequestEmailSentTime') {
          const aValue = a.contractor?.agreementRequestEmailSentTime ?? -1
          const bValue = b.contractor?.agreementRequestEmailSentTime ?? -1
          return sortItem.type === 'asc' ? aValue - bValue : bValue - aValue
        }
        if (sortItem.col === 'registrationStatus') {
          const loadApplicationValue = (s: RegistrationStatus | undefined) => {
            switch (s) {
              case 'waiting': {
                return 1
              }
              case 'registered': {
                return 2
              }
              case 'agreed': {
                return 3
              }
              default: {
                return 0
              }
            }
          }

          const aValue = loadApplicationValue(a.registrationStatus)
          const bValue = loadApplicationValue(b.registrationStatus)
          return sortItem.type === 'asc' ? aValue - bValue : bValue - aValue
        }
        if (sortItem.col === 'proxyRegister') {
          const aValue = a.isEnableProxySelectRegistration ?? false
          const bValue = b.isEnableProxySelectRegistration ?? false
          return sortItem.type === 'asc' ? (aValue ? 1 : -1) : bValue ? 1 : -1
        }

        return 0
      })
    },
    [sortItem]
  )

  const isEnabledFilter = useMemo(() => {
    return (
      !!searchWord ||
      (!!filteredRoomIds && filteredRoomIds.length > 0) ||
      (!!itemBasedFiltering && Object.values(itemBasedFiltering).some(Boolean)) ||
      !!rangeFilterRegisterLimitAt ||
      !!sortItem
    )
  }, [searchWord, filteredRoomIds, itemBasedFiltering, rangeFilterRegisterLimitAt, sortItem])

  const setApplicationRoomIds = (roomIds: string[]) => {
    // 住戸一覧のフィルタリング状態をリセットする
    resetFilteringItems()

    // 承認対象の住戸のみを表示し、かつ選択状態にする
    setFilteredRoomIds(roomIds)
    roomTableStore.setSelectedRoomIds(roomIds)
  }

  // 編集時に使用するroomsをフィルタリング条件に応じて更新する
  const updateWatchedRoomsFilter = useCallback(
    (input: UpdateWatchedRoomsInput) => {
      // TODO:
      //  useRoomTableFiltering の itemBasedFiltering・ rangeFilterRegisterLimitAt を利用するとundefinedになってしまっていることがあるため、inputから取得している
      //  分かりづらく追いづらい修正したい
      const {
        watch,
        wings,
        roomTypes,
        setValue,
        itemBasedFiltering: propertyFiltering,
        rangeFilterRegisterLimitAt: propertyRangeFilterRegisterLimitAt,
      } = input

      // 住戸一覧のフィルタリングを実施するためRoomEntityに変換。ただし、入力中のものなど情報が足りないものも出てくるため Partial<RoomEntity> にする
      const entities = watch('items').map(item =>
        RoomTranslator.toEntityFromFormEntity({ formEntity: item, wings, roomTypes })
      )

      // 検索結果に引っかかった住戸のidを取得
      const filteredEntityIds = new Set(
        loadFilteredRooms<Partial<RoomEntity>[]>({
          rooms: entities,
          itemBasedFiltering: propertyFiltering,
          rangeFilterRegisterLimitAt: propertyRangeFilterRegisterLimitAt,
        })
          .map(item => item.id)
          .filter((id): id is string => id !== undefined)
      )

      // 検索条件とマッチする住戸はisFilteredをtrueに画面に表示する
      for (const [index, item] of watch('items').entries()) {
        setValue(`items.${index}.isFiltered`, filteredEntityIds.has(item.id))
      }
    },

    // 無限ループが起きてしまうため、roomTableStoreValue以外は依存させない
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomTableStoreValue]
  )

  const batchUpdateWatchedRoomsWithText = (input: BatchUpdateWatchedRoomsWithTextInput) => {
    const { watch, setValue, text, key } = input

    for (const [index, item] of watch('items').entries()) {
      const isLocked = ['reviewing', 'approved'].includes(
        item.publishApplicationRequestStatus ?? ''
      )
      // フィルターで表示されていて、ロックがかかっていないもののみ更新する
      if (item.isFiltered && !isLocked) {
        if (key === 'roomName') {
          setValue(`items.${index}.name`, text)
        }
        if (key === 'floor') {
          setValue(`items.${index}.floor`, text)
        }
        if (key === 'memo') {
          setValue(`items.${index}.memo`, text)
        }
      }
    }
  }

  const batchUpdateWatchedRoomsWithDate = (input: BatchUpdateWatchedRoomsWithDateInput) => {
    const { watch, setValue, date, key } = input

    for (const [index, item] of watch('items').entries()) {
      const isLocked = ['reviewing', 'approved'].includes(
        item.publishApplicationRequestStatus ?? ''
      )

      // フィルターで表示されていて、ロックがかかっていないもののみ更新する
      if (item.isFiltered && !isLocked && key === 'registrationDueDate') {
        setValue(`items.${index}.registrationDueDate`, date)
      }
    }
  }

  const batchUpdateWatchedRoomsWithBoolean = useCallback(
    (input: BatchUpdateWatchedRoomsWithBooleanInput) => {
      const { watch, setValue, value, key } = input

      for (const [index, item] of watch('items').entries()) {
        const isLocked = ['reviewing', 'approved'].includes(
          item.publishApplicationRequestStatus ?? ''
        )

        // フィルターで表示されていて、ロックがかかっていないもののみ更新する
        if (item.isFiltered && !isLocked && key === 'isSelectEnabled') {
          setValue(`items.${index}.isSelectEnabled`, value)
        }
      }
    },
    []
  )

  const batchUpdateWatchedRoomsWithSelectMaster = (
    input: BatchUpdateWatchedRoomsWithSelectMasterInput
  ) => {
    const { watch, setValue, selectMaster, isNewValue } = input

    for (const [index, item] of watch('items').entries()) {
      const isLocked = ['reviewing', 'approved'].includes(
        item.publishApplicationRequestStatus ?? ''
      )

      // フィルターで表示されていて、ロックがかかっていないもののみ更新する
      if (item.isFiltered && !isLocked) {
        const keyName = `items.${index}.selectMasters` as `items.${number}.selectMasters`

        // 回答、子回答関係なく今回選択した回答全てのidを取得
        const targetOptionMasterIds = selectMaster.options.reduce((previous: string[], current) => {
          // 親の回答のidを追加
          const returnValue = [...previous, current.id]

          // 子回答がある場合は子回答のidを追加
          return current.children
            ? [...returnValue, ...current.children.map(child => child.id)]
            : returnValue
        }, [])

        // 選択されたセレクト以外のセレクト情報を取得
        const excludedItems = watch(keyName).filter(v => v.id !== Number(selectMaster.id))

        if (isNewValue) {
          // 新たにセレクトが選択がされた場合

          setValue(keyName, [
            ...excludedItems,
            // 回答・子回答を含めて全て選択したことに
            {
              id: Number.parseInt(selectMaster.id),
              selectOptionMasterIds: targetOptionMasterIds.map(Number),
            },
          ])
        } else {
          // 選択を解除した場合

          const hasBeenSaved = watch(keyName).find(
            v => v.id === Number.parseInt(selectMaster.id)
          )?.hasBeenSaved

          setValue(keyName, [
            ...excludedItems,
            {
              id: Number.parseInt(selectMaster.id),
              selectOptionMasterIds: [],
              isDeleted: true,
              hasBeenSaved,
            },
          ])
        }
      }
    }
  }

  return {
    selectedRoomIds: roomTableStoreValue.selectedRoomIds,
    toggleSelectedRoomId: roomTableStore.toggleSelectedRoomId,
    resetSelectedRoomIds: roomTableStore.resetSelectedRoomIds,
    loadExcludeKeysFromFilteredRooms,
    getFilteredRooms,
    getSortedRooms,
    isEnabledFilter,
    setApplicationRoomIds,
    isShowAllRoomColumns: roomTableStoreValue.isShowAllRoomColumns,
    updateShowAllRoomColumns: roomTableStore.updateShowAllRoomColumns,
    isShowAllSelectMasterColumns: roomTableStoreValue.isShowAllSelectMasterColumns,
    updateShowAllSelectMasterColumns: roomTableStore.updateShowAllSelectMasterColumns,
    isShowAllConstructorColumns: roomTableStoreValue.isShowAllConstructorColumns,
    updateShowAllConstructorColumns: roomTableStore.updateShowAllConstructorColumns,

    selectedCell: roomTableStoreValue.selectedCell,
    updateSelectedCell: roomTableStore.updateSelectedCell,
    resetSelectedCell: roomTableStore.resetSelectedCell,

    setSelectedRoomIds: roomTableStore.setSelectedRoomIds,
    updateWatchedRoomsFilter,
    batchUpdateWatchedRoomsWithText,
    batchUpdateWatchedRoomsWithDate,
    batchUpdateWatchedRoomsWithBoolean,
    batchUpdateWatchedRoomsWithSelectMaster,
  }
}
